import type { IconCode } from "@components/Atoms/Icon/LucideIcon";
import toggleGrouping from "@ext/catalog/views/logic/utils/toggleGrouping";
import t from "@ext/localization/locale/translate";
import { isMarkdownText } from "@ext/markdown/elements/pasteMarkdown/logic/handlePasteMarkdown";
import PropertyServiceProvider from "@ext/properties/components/PropertyService";
import { type PropertyID, SystemProperties } from "@ext/properties/models";
import { isComplexProperty } from "@ext/templates/models/properties";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuSearchItem,
	DropdownMenuSeparator,
	DropdownMenuTriggerButton,
	useSearchableMenu,
} from "@ui-kit/Dropdown";
import { Icon } from "@ui-kit/Icon";
import { useCallback, useMemo } from "react";

interface AddGroupingProps {
	groupBy: PropertyID[];
	disabled?: boolean;
	onChange: (groupBy: PropertyID[]) => void;
}

/**
 * Hierarchy is a system property, and PropertyService strips those from the catalog list,
 * so the default grouping is added to the menu here rather than coming from the store.
 */
export const AddGrouping = ({ groupBy, disabled, onChange }: AddGroupingProps) => {
	const { properties: catalogProperties } = PropertyServiceProvider.value;

	const { search, setSearch, contentRef, inputRef, handleContentKeyDown, handleInputKeyDown, filterItems } =
		useSearchableMenu();

	const items = useMemo(() => {
		const hierarchy = {
			id: SystemProperties.hierarchy,
			name: t("catalog.views.group-by-hierarchy"),
			icon: "folder-tree",
			label: t("catalog.views.group-by-hierarchy"),
			checked: groupBy.includes(SystemProperties.hierarchy),
		};

		const properties = Array.from(catalogProperties.values())
			.filter((property) => !isComplexProperty[property.type] && !isMarkdownText(property.value?.[0]))
			.map((property) => ({
				id: property.id,
				name: property.name,
				icon: property.icon,
				label: property.name,
				checked: groupBy.includes(property.id),
			}));

		return filterItems([hierarchy, ...properties]);
	}, [catalogProperties, filterItems, groupBy]);

	const onCheckboxChange = useCallback(
		(id: PropertyID) => onChange(toggleGrouping(groupBy, id)),
		[groupBy, onChange],
	);

	return (
		<DropdownMenu>
			<DropdownMenuTriggerButton
				className="ml-auto h-auto p-0"
				data-testid="catalog-view-add-grouping-trigger"
				disabled={disabled}
				size="sm"
				variant="text"
			>
				<Icon icon="plus" size="md" />
			</DropdownMenuTriggerButton>
			<DropdownMenuContent onKeyDown={handleContentKeyDown} ref={contentRef}>
				<DropdownMenuSearchItem
					onChange={(e) => setSearch(e.target.value)}
					onClick={(e) => e.stopPropagation()}
					onKeyDown={handleInputKeyDown}
					placeholder={t("search.placeholder")}
					ref={inputRef}
					value={search}
				/>
				<DropdownMenuSeparator />
				{items.map((item) => (
					<DropdownMenuCheckboxItem
						checked={item.checked}
						key={item.id}
						onSelect={() => onCheckboxChange(item.id)}
					>
						{item.icon ? <Icon icon={item.icon as IconCode} /> : <div className="w-4 h-4" />}
						{item.name}
					</DropdownMenuCheckboxItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
